import { IcoRoute, IcoTrash } from "assets/icons";
import IcoCalendarThin from "assets/icons/ico-calendar-thin";
import IcoFileEdit from "assets/icons/ico-file-edit";
import IcoRoad from "assets/icons/ico-road";
import IcoProfileRounded from "assets/icons/ico-user-thin-circle";
import colors from "colors";
import { Button, Loading } from "components";
import { BackofficeCancelApplicationDrawer } from "components/BackofficeCancelApplicationDrawer";
import { DateTimeHelper } from "helpers";
import { UserAuthorities } from "models/auth/types";
import ApplicationDetailData, {
	FieldsSection
} from "pages/Application/ApplicationDetail/modules/ApplicationDetailData";
import { useCurrentUser } from "providers/CurrentUserProvider";
import { useMemo, useState } from "react";
import { TabComponentProps } from "../../types";
import "./styles.scss";

export default function ApplicationPageDetails(props: TabComponentProps) {
	const { application } = props;
	const [cancelDrawerOpen, setCancelDrawerOpen] = useState(false);
	const openCancelDrawer = () => setCancelDrawerOpen(true);

	const user = useCurrentUser();

	const canCancel = useMemo(() => {
		return (
			!application?.isInFinalStatus() &&
			user?.hasPermission(UserAuthorities.CREATE)
		);
	}, [application, user]);

	const applicationDetails = useMemo((): FieldsSection[] => {
		if (!application) return [];

		// TODO: Remover caso valores LEGADO sejam atualizados para Não Classificado no banco de dados
		const projectProgressStatus =
			application.projectProgressStatus === "LEGADO"
				? "Não Classificado"
				: application.projectProgressStatus
				? application.projectProgressStatus.charAt(0).toUpperCase() +
				  application.projectProgressStatus.substring(1).toLowerCase()
				: "";

		const backofficeDetails = [
			{
				title: "",
				nameValueFields: [
					{
						name: "Tipo de intervenção",
						value: application?.interventionTypeName
					},
					{
						name: "Categoria",
						value: application?.interventionTypeCategoryName
					},
					{
						name: "Processo",
						value: application?.applicationProcessCode
					},
					{
						name: "Status",
						value: application?.statusInternalLabel
					},
					{
						name: "Etapa do processo",
						value: projectProgressStatus
					},
					{
						name: "Data da abertura do processo",
						value:
							application?.issuedAt &&
							DateTimeHelper.formatDate(
								new Date(application.issuedAt),
								"dd/MM/yyyy"
							)
					},
					{
						name: "Data de início do protocolo",
						value:
							application?.analysisStartDatetime &&
							DateTimeHelper.formatDateString(
								application.analysisStartDatetime
							),
						condition: !!application?.analysisStartDatetime
					},
					{
						name: "Prazo para análise",
						value:
							application?.analysisLimitDatetime &&
							DateTimeHelper.formatDateString(
								application.analysisLimitDatetime
							),
						condition: !!application?.analysisLimitDatetime
					}
				]
			}
		];
		return [...backofficeDetails];
	}, [application]);

	return !application ? (
		<Loading />
	) : (
		<div className="flex flex-1 flex-wrap md:flex-nowrap gap-6 pb-5">
			<div className="flex-1">
				<div className="section-container">
					<div className="section-name">
						<span className="flex items-center gap-2">
							<IcoCalendarThin />
							<p>Detalhes da solicitação</p>
						</span>
					</div>
					<div className="px-8">
						<ApplicationDetailData applicationData={applicationDetails} />
					</div>
					{canCancel && (
						<>
							<div className="md:border-t border-t-neutral-high-100 mx-8 py-4 md:pflex flex-row justify-between items-center">
								<Button
									hierarchy="secondary"
									size="small"
									disabled={!application}
									onClick={openCancelDrawer}
									className="cancel-button"
								>
									<IcoTrash color={colors["feedback-negative"]["pure-500"]} />{" "}
									<p>Cancelar solicitação</p>
								</Button>
							</div>
							{application?.id && (
								<BackofficeCancelApplicationDrawer
									isOpen={cancelDrawerOpen}
									onClose={() => setCancelDrawerOpen(false)}
									application={application}
								/>
							)}
						</>
					)}
				</div>
				<div className="section-container mt-4">
					<div className="section-name">
						<span className="flex items-center gap-2">
							<IcoRoad />
							<p>Detalhes da Rodovia</p>
						</span>
					</div>
					<div className="px-8 pb-4">
						<ApplicationDetailData
							applicationData={application.highwayDetails}
						/>
					</div>
				</div>
				<div className="section-container mt-4">
					<div className="section-name">
						<span className="flex items-center gap-2">
							<IcoProfileRounded />
							<p>Dados do proprietário</p>
						</span>
					</div>
					<div className="px-8 pb-1">
						<ApplicationDetailData
							applicationData={application.applicantBackOfficeDetails}
						/>
					</div>
				</div>
			</div>
			<div className="flex-1">
				<div className="section-container">
					<div className="section-name">
						<span className="flex items-center gap-2">
							<IcoFileEdit />
							<p>Descrição da solicitação</p>
						</span>
					</div>
					<div className="description-content px-8 py-8">
						{application.description}
					</div>
				</div>
				<div className="section-container mt-4">
					<div className="section-name">
						<span className="flex items-center gap-2">
							<IcoRoute />
							<p>Local de intervenção</p>
						</span>
					</div>
					<div className="px-8 pb-1">
						<ApplicationDetailData
							applicationData={application.interventionDetails}
						/>
					</div>
				</div>
				<div className="section-container mt-4">
					<div className="section-name">
						<p>Dados do Solicitante</p>
					</div>
					<div className="px-8 pb-1">
						<ApplicationDetailData
							applicationData={application.applicantData}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
